import { useEffect, useState } from "react";
import { track } from "../../analytics";
import { Link, useParams } from "react-router-dom";
import type { PublicPlaylistDetail } from "@gooner/shared";
import { api, apiErrorOf, userApi } from "../../lib/api";
import { useSession } from "../../lib/session";
import { usePlayCollection } from "../../lib/playCollection";
import { AuthModal } from "../components/AuthModal";
import { GoonlordsWordmark } from "../ui/Logo";
import { Button, cx } from "../ui/primitives";
import { IconCheck } from "../ui/icons";
import { CollectionView, relativeDate } from "../collections/CollectionView";
import { GoldName } from "../ui/Gold";
import { GoldDialog } from "../components/GoldDialog";
import { trackGoldGate, useGoldGate } from "../../lib/gold";
import { AdSlot } from "../components/AdSlot";

/**
 * Public detail page for a Browse collection. Viewable signed-out (NOT gated
 * behind AuthedPage). Renders the shared CollectionView with the public slot: an "add to
 * my collections" clone action in the header. Each clip's crown, "+" and View come with
 * the card (ClipCard), the same as on Browse.
 */
export function CollectionDetail() {
  const { id } = useParams<{ id: string }>();
  const user = useSession((s) => s.user);
  const playCollection = usePlayCollection();

  const [detail, setDetail] = useState<PublicPlaylistDetail | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  const [addState, setAddState] = useState<"idle" | "adding" | "added">("idle");
  const [goldGate, setGoldGate] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const gate = useGoldGate();

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setStatus("loading");
    api
      .getCollection(id)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  async function addToMine() {
    if (!user) {
      setAuthOpen(true);
      return;
    }
    if (!detail || addState === "adding") return;
    // Pre-empt, the layout gate's precedent: a clone larger than the viewer's
    // per-collection ceiling would mint an empty copy and then refuse the 101st add —
    // a half-cloned collection nobody asked for. Ask first; the server's 409 stays the
    // backstop below.
    if (detail.items.length > gate.maxClipsPerCollection) {
      trackGoldGate("collections");
      setGoldGate(true);
      return;
    }
    setAddState("adding");
    try {
      const pl = await userApi.createPlaylist(detail.name, "clone");
      // ONE act, not one added_to_collection per clip: the per-clip adds are silent
      // and the clone is counted once, with its size, after every add has landed.
      await Promise.all(
        detail.items.map((c) => userApi.addToPlaylist(pl.id, c, { silent: true })),
      );
      track("collection_cloned", { item_count: detail.items.length });
      setAddState("added");
    } catch (e) {
      // The clone used to fail into silence. A Gold cap opens the upsell; any other
      // refusal at least returns the button so the act can be retried.
      if (apiErrorOf(e)?.goldUpsell) {
        trackGoldGate("collections");
        setGoldGate(true);
      }
      setAddState("idle");
    }
  }

  if (status === "loading") {
    return <Centered>Loading…</Centered>;
  }
  if (status === "error" || !detail) {
    return (
      <Centered>
        Collection not found.{" "}
        <Link
          to="/browse"
          className="text-[var(--color-accent-soft)] hover:underline"
        >
          Back to Browse
        </Link>
      </Centered>
    );
  }

  return (
    <div className="mx-auto max-w-6xl p-6">
      <CollectionView
        key={id}
        backTo={{ to: "/collections", label: "Browse collections" }}
        title={detail.name}
        meta={
          <>
            {detail.editorsChoice ? (
              <span className="inline-flex items-center gap-1.5">
                <GoonlordsWordmark height={18} />
                <span>· Editor's Choice</span>
              </span>
            ) : (
              <GoldName name={<>by {detail.ownerName ?? "someone"}</>} gold={detail.ownerGold} />
            )}
            <span aria-hidden>·</span>
            <span>
              {detail.clipCount} clip{detail.clipCount === 1 ? "" : "s"}
            </span>
            <span aria-hidden>·</span>
            <span>Created {relativeDate(detail.createdAt)}</span>
          </>
        }
        description={detail.description}
        items={detail.items}
        gridBreak={<AdSlot slot="collectionPage" size={{ width: 300, height: 250 }} />}
        onPlay={(layout) =>
          id && void playCollection({ id, name: detail.name }, layout)
        }
        headerActions={
          <Button
            variant="subtle"
            onClick={addToMine}
            disabled={addState === "adding"}
            className={cx(addState === "added" && "text-emerald-400")}
          >
            {addState === "added" ? (
              <>
                <IconCheck width={15} height={15} /> Added
              </>
            ) : addState === "adding" ? (
              "Adding…"
            ) : (
              "Add to my collections"
            )}
          </Button>
        }
      />
      {authOpen && <AuthModal onClose={() => setAuthOpen(false)} />}
      {goldGate && <GoldDialog source="collections" onClose={() => setGoldGate(false)} />}
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-6xl p-6">
      <p className="py-20 text-center text-sm text-zinc-500">{children}</p>
    </div>
  );
}
